import React, { useState, useEffect } from 'react';
import FilterNav from './FilterNav';

const GlobalMarket = () => {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGlobalData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/global`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch global data (status ${response.status})`);
        }
        
        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        }
        
        setMarketData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching global market data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGlobalData();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchGlobalData, 300000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(num);
  };

  const formatPercentage = (num) => {
    return `${num?.toFixed(2)}%`;
  };

  if (error) return <div className="text-center py-10 text-red-500">{error}</div>;
  if (!marketData) return <div className="text-center py-10">No data available</div>;

  return (
    <div className="home_wrapper">
      <FilterNav />
      <div className="container mx-auto px-4 py-8">
        <h3 className="text-2xl font-bold mb-6">Global Crypto Market Overview</h3>
        
        {/* Market Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm">Total Market Cap</h3>
            <p className="text-2xl font-bold">{formatNumber(marketData.market_cap)}</p>
            <p className={`updown_txt ${
              marketData.market_cap_change >= 0 ? 'upcoin' : 'downcoin'
            }`}>
              {marketData.market_cap_change >= 0 ? '↑' : '↓'} {formatPercentage(marketData.market_cap_change)} (24h)
            </p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm">24h Trading Volume</h3>
            <p className="text-2xl font-bold">{formatNumber(marketData.volume)}</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm">Bitcoin Dominance</h3>
            <p className="text-2xl font-bold">{formatPercentage(marketData.btc_dominance)}</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm">Ethereum Dominance</h3>
            <p className="text-2xl font-bold">{formatPercentage(marketData.eth_dominance)}</p>
          </div>
        </div>
        
        {/* Market Statistics */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-bold mb-4">Market Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <h3 className="text-gray-500 text-sm">Active Cryptocurrencies</h3>
              <p className="text-lg font-semibold">{marketData.active_cryptos}</p>
            </div>
            <div>
              <h3 className="text-gray-500 text-sm">Active Markets</h3>
              <p className="text-lg font-semibold">{marketData.markets}</p>
            </div>
          </div>
        </div>
        
        {/* Top Coins Table */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4">Top 10 Cryptocurrencies</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Coin</th>
                  <th className="px-4 py-2 text-right">Price</th>
                  <th className="px-4 py-2 text-right">Market Cap</th>
                </tr>
              </thead>
              <tbody>
                {marketData.top_coins.map((coin, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2">{index + 1}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center">
                        <span className="font-medium">{coin.name}</span>
                        <span className="text-gray-500 ml-2">{coin.symbol}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6
                      }).format(coin.price)}
                    </td>
                    <td className="px-4 py-2 text-right">{formatNumber(coin.market_cap)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalMarket;